import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, ChevronLeft, ChevronRight, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format, addDays, startOfWeek, isSameDay, addWeeks } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useCalendarAvailability } from "@/hooks/useCalendarAvailability";

interface CalendarStepProps {
  selectedDate: Date | null;
  selectedTime: string | null;
  onSelectDate: (date: Date) => void;
  onSelectTime: (time: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  loading: boolean;
}

const CalendarStep = ({
  selectedDate,
  selectedTime,
  onSelectDate,
  onSelectTime,
  onBack,
  onSubmit,
  loading,
}: CalendarStepProps) => {
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
  const { availableSlots, loading: slotsLoading, fetchAvailability } = useCalendarAvailability();

  // Fetch availability when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(selectedDate);
    }
  }, [selectedDate, fetchAvailability]);
  const today = new Date();
  const baseWeekStart = startOfWeek(today, { weekStartsOn: 1 });
  const weekStart = addWeeks(baseWeekStart, currentWeekOffset);
  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)); // Only weekdays

  const handlePrevWeek = () => {
    if (currentWeekOffset > 0) {
      setCurrentWeekOffset((prev) => prev - 1);
    }
  };

  const handleNextWeek = () => {
    if (currentWeekOffset < 4) { // Limit to 4 weeks ahead
      setCurrentWeekOffset((prev) => prev + 1);
    }
  };

  const isDateDisabled = (date: Date) => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    return date < todayStart;
  };

  const canSubmit = selectedDate && selectedTime;

  const morningSlots = availableSlots.filter((t) => parseInt(t) < 12).map(t => ({ time: t, period: "Manhã" }));
  const afternoonSlots = availableSlots.filter((t) => parseInt(t) >= 12).map(t => ({ time: t, period: "Tarde" }));

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
          Agende sua reunião
        </h2>
        <p className="text-muted-foreground text-base">
          Escolha o melhor dia e horário para conversarmos
        </p>
      </div>

      {/* Calendar Card */}
      <div className="bg-card border border-border/50 rounded-2xl overflow-hidden shadow-sm">
        {/* Month Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-accent/30 border-b border-border/30">
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrevWeek}
            disabled={currentWeekOffset === 0}
            className="h-8 w-8 rounded-full hover:bg-background"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-semibold text-foreground capitalize">
            {format(weekStart, "MMMM yyyy", { locale: ptBR })}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNextWeek}
            disabled={currentWeekOffset >= 4}
            className="h-8 w-8 rounded-full hover:bg-background"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Days Row */}
        <div className="grid grid-cols-5 gap-1 p-3">
          {weekDays.map((day) => {
            const isDisabled = isDateDisabled(day);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const isToday = isSameDay(day, today);

            return (
              <button
                key={day.toISOString()}
                onClick={() => !isDisabled && onSelectDate(day)}
                disabled={isDisabled}
                className={`relative flex flex-col items-center py-3 px-2 rounded-xl transition-all ${
                  isDisabled
                    ? "opacity-30 cursor-not-allowed"
                    : isSelected
                    ? "bg-primary text-primary-foreground shadow-md scale-105"
                    : "hover:bg-accent cursor-pointer"
                }`}
              >
                <span className={`text-[10px] font-medium uppercase ${
                  isSelected ? "text-primary-foreground/80" : "text-muted-foreground"
                }`}>
                  {format(day, "EEE", { locale: ptBR })}
                </span>
                <span className={`text-xl font-semibold mt-1 ${
                  isSelected ? "text-primary-foreground" : ""
                }`}>
                  {format(day, "d")}
                </span>
                {isToday && !isSelected && (
                  <div className="absolute bottom-1 w-1.5 h-1.5 rounded-full bg-primary" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time Slots */}
      {selectedDate && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <span>Horários disponíveis</span>
            {slotsLoading && <Loader2 className="h-3 w-3 animate-spin" />}
          </div>

          {slotsLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Verificando disponibilidade...
            </div>
          ) : availableSlots.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground text-sm">
              Nenhum horário disponível nesta data. Tente outro dia.
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {availableSlots.map((time) => (
                <button
                  key={time}
                  onClick={() => onSelectTime(time)}
                  className={`py-3 px-4 rounded-2xl text-sm font-medium transition-all border ${
                    selectedTime === time
                      ? "bg-primary text-primary-foreground border-primary shadow-md"
                      : "bg-card border-border/60 hover:border-primary/40 hover:shadow-sm"
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Selected summary */}
      {canSubmit && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl p-4"
        >
          <div className="bg-primary/10 rounded-lg p-2">
            <Calendar className="h-5 w-5 text-primary" />
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">Reunião agendada para </span>
            <span className="font-semibold text-foreground">
              {format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR })}
            </span>
            <span className="text-muted-foreground"> às </span>
            <span className="font-semibold text-primary">{selectedTime}</span>
          </div>
        </motion.div>
      )}

      {/* Buttons */}
      <div className="flex gap-3 pt-2">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={loading}
          className="h-14 px-6 rounded-2xl text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!canSubmit || loading}
          className="flex-1 h-14 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base transition-all disabled:opacity-50"
        >
          {loading ? "Enviando..." : "Confirmar agendamento"}
        </Button>
      </div>
    </motion.div>
  );
};

export default CalendarStep;
